import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { PasswordGate, ProtectedRoute, RequireRole } from "@/shared/auth/ProtectedRoute";
import { AppShell } from "@/widgets/AppShell";
import { Spinner } from "@/shared/ui/Spinner";
import { ChunkErrorBoundary, RouteError } from "./RouteError";

const LoginPage     = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const ControlCenterPage = lazy(() => import("@/pages/ControlCenterPage").then((m) => ({ default: m.ControlCenterPage })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const KpiPage = lazy(() =>
  import("@/pages/KpiPage").then((m) => ({ default: m.KpiPage })),
);
const SitesPage      = lazy(() => import("@/pages/SitesPage").then((m) => ({ default: m.SitesPage })));
const SiteDetailPage = lazy(() => import("@/pages/SiteDetailPage").then((m) => ({ default: m.SiteDetailPage })));
const SensorsPage   = lazy(() => import("@/pages/SensorsPage").then((m) => ({ default: m.SensorsPage })));
const AlertsPage    = lazy(() => import("@/pages/AlertsPage").then((m) => ({ default: m.AlertsPage })));
const AnomalyPage   = lazy(() => import("@/pages/AnomalyPage").then((m) => ({ default: m.AnomalyPage })));
const SettingsPage    = lazy(() => import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ProfilePage     = lazy(() => import("@/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const DigitalTwinPage = lazy(() => import("@/pages/DigitalTwinPage").then((m) => ({ default: m.DigitalTwinPage })));
const RiskPage      = lazy(() => import("@/pages/RiskPage").then((m) => ({ default: m.RiskPage })));
const ScenariosPage = lazy(() => import("@/pages/ScenariosPage").then((m) => ({ default: m.ScenariosPage })));
const ReportsPage   = lazy(() => import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const BlueprintsPage = lazy(() => import("@/pages/BlueprintsPage").then((m) => ({ default: m.BlueprintsPage })));
const DevicesPage    = lazy(() => import("@/pages/DevicesPage").then((m) => ({ default: m.DevicesPage })));
const EvacuationPage = lazy(() => import("@/pages/EvacuationPage").then((m) => ({ default: m.EvacuationPage })));
const GasDbPage     = lazy(() => import("@/pages/GasDbPage").then((m) => ({ default: m.GasDbPage })));
const CapturesPage  = lazy(() => import("@/pages/CapturesPage").then((m) => ({ default: m.CapturesPage })));
const CamerasPage   = lazy(() => import("@/pages/CamerasPage").then((m) => ({ default: m.CamerasPage })));
const UsersPage       = lazy(() => import("@/pages/admin/UsersPage").then((m) => ({ default: m.UsersPage })));
const OrgsPage        = lazy(() => import("@/pages/admin/OrgsPage").then((m) => ({ default: m.OrgsPage })));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePasswordPage").then((m) => ({ default: m.ChangePasswordPage })));

/**
 * Every lazy page goes through here, so this is also where a **stale bundle**
 * is caught: after a deploy the hashed chunk an open tab asks for is gone, the
 * dynamic import rejects, and without a boundary React Router's default
 * developer screen replaced the entire console. `ChunkErrorBoundary` keeps the
 * shell mounted and reloads the tab once onto the new build.
 */
function Suspended({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense
        fallback={
          <div className="grid place-items-center h-[60dvh] text-[var(--fg-subtle)]">
            <Spinner size={20} />
          </div>
        }
      >
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <LoginPage />
      </Suspended>
    ),
  },
  {
    element: <ProtectedRoute />,
    // Catch-all: anything the per-page ChunkErrorBoundary doesn't own (loaders,
    // the shell itself) lands here instead of React Router's default screen.
    errorElement: <RouteError />,
    children: [
      // Forced password change lives OUTSIDE the app shell + password gate so a
      // must-change user can actually reach it (the gate redirects here).
      { path: "change-password", element: <Suspended><ChangePasswordPage /></Suspended> },
      {
        element: <PasswordGate />,
        children: [
          {
            element: <AppShell />,
            children: [
              // 홈 = 통합 관제. 기존 통계 대시보드는 /overview 로 이동했다
              // (북마크 보호를 위해 경로 변경은 이 두 건뿐).
              { index: true,         element: <Suspended><ControlCenterPage /></Suspended> },
              { path: "overview",    element: <Suspended><DashboardPage /></Suspended> },
              // 협약 KPI 달성 현황 — 최종평가 관문이라 분석 그룹 최상단.
              { path: "kpi",         element: <Suspended><KpiPage /></Suspended> },
              { path: "sites",       element: <Suspended><SitesPage /></Suspended> },
              { path: "sites/:id",   element: <Suspended><SiteDetailPage /></Suspended> },
              { path: "sensors",     element: <Suspended><SensorsPage /></Suspended> },
              { path: "alerts",      element: <Suspended><AlertsPage /></Suspended> },
              // 관제 화면(NOC)은 통합 관제로 통합됨 — 기존 딥링크·`?kiosk=1`은
              // 같은 화면으로 유지한다.
              { path: "noc",         element: <Suspended><ControlCenterPage /></Suspended> },
              { path: "risk",        element: <Suspended><RiskPage /></Suspended> },
              { path: "anomaly",     element: <Suspended><AnomalyPage /></Suspended> },
              { path: "dispersion",  element: <Suspended><DigitalTwinPage variant="simulator" /></Suspended> },
              { path: "evacuation",  element: <Suspended><EvacuationPage /></Suspended> },
              { path: "twin",        element: <Suspended><DigitalTwinPage /></Suspended> },
              // 도면·캡처·CCTV = 전 인증사용자 열람 가능(08 매트릭스 ✂). 관리
              // 액션(업로드·삭제·편집)만 페이지 내 버튼에서 safety_manager+로 게이팅.
              { path: "blueprints",  element: <Suspended><BlueprintsPage /></Suspended> },
              { path: "captures",    element: <Suspended><CapturesPage /></Suspended> },
              // CCTV = 품목5(비전 AI) 소관이라 과제 스코프 밖이지만, 현장 시각확인
              // 용도로 계속 쓰이므로 메뉴·라우트 유지(감지 그룹). 열람은 전 인증사용자.
              { path: "cameras",     element: <Suspended><CamerasPage /></Suspended> },
              { path: "gas-db",      element: <Suspended><GasDbPage /></Suspended> },
              // 현장 장치(액추에이터) 프로파일 — 열람은 관제요원 이상, 배선
              // 변경·시험 버튼만 페이지 안에서 safety_manager+로 게이팅.
              { path: "devices",     element: <Suspended><DevicesPage /></Suspended> },
              { path: "scenarios",   element: <Suspended><ScenariosPage /></Suspended> },
              { path: "reports",     element: <Suspended><ReportsPage /></Suspended> },
              { path: "settings",    element: <Suspended><SettingsPage /></Suspended> },
              // 내 계정 = 나에 관한 것(정보·알림·보안), 설정 = 시스템·조직에
              // 관한 것. 계정 메뉴의 두 항목이 서로 다른 곳에 도착한다.
              { path: "profile",     element: <Suspended><ProfilePage /></Suspended> },

              // ── 사용자 관리 — 고객사 관리자 이상 ─────────────────────
              {
                element: <RequireRole min="org_admin" />,
                children: [
                  { path: "admin/users", element: <Suspended><UsersPage /></Suspended> },
                ],
              },
              // ── 조직 관리 — 플랫폼 관리자 전용 ───────────────────────
              {
                element: <RequireRole min="super_admin" />,
                children: [
                  { path: "admin/orgs", element: <Suspended><OrgsPage /></Suspended> },
                ],
              },

              { path: "*",           element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
